<template>
  <b-sidebar
    :visible="value"
    right
    shadow
    backdrop
    width="700px"
    bg-variant="white"
    no-header
    @hidden="onHidden"
  >
    <template v-if="loading">
      <div class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Loading ticket...</p>
      </div>
    </template>

    <template v-else-if="ticket">
      <!-- Header -->
      <div class="sidebar-header bg-primary text-white p-3">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-1">{{ ticket.ticketNumber }}</h5>
            <p class="mb-0">{{ ticket.name }}</p>
          </div>
          <div class="d-flex">
            <b-button
              variant="light"
              size="sm"
              class="mr-2"
              @click="$emit('navigate-to-details', ticket.id)"
              v-b-tooltip.hover
              title="Open Full Details"
            >
              <i class="fa fa-external-link"></i>
            </b-button>
            <b-button
              variant="light"
              size="sm"
              @click="closeSidebar"
            >
              <i class="fa fa-times"></i>
            </b-button>
          </div>
        </div>
      </div>

      <!-- Ticket Info Summary -->
      <div class="p-3 border-bottom bg-light">
        <b-row>
          <b-col cols="4">
            <small class="text-muted d-block">Status</small>
            <b-badge :variant="getStatusVariant(ticket.status, ticket.subStatus)">
              {{ getStatusLabel(ticket.status, ticket.subStatus) }}
            </b-badge>
          </b-col>
          <b-col cols="4">
            <small class="text-muted d-block">Priority</small>
            <b-badge :variant="getPriorityVariant(ticket.priority)">
              {{ getPriorityLabel(ticket.priority) }}
            </b-badge>
          </b-col>
          <b-col cols="4">
            <small class="text-muted d-block">Created By</small>
            <span class="small">{{ ticket.userFullName }}</span>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col cols="4">
            <small class="text-muted d-block">Type</small>
            <span class="small">{{ ticket.type }}</span>
          </b-col>
          <b-col cols="4">
            <small class="text-muted d-block">Created</small>
            <span class="small">{{ formatDate(ticket.createDateTimeUtc) }}</span>
          </b-col>
          <b-col cols="4" v-if="ticket.completedDateTimeUtc">
            <small class="text-muted d-block">Closed</small>
            <span class="small">{{ formatDate(ticket.completedDateTimeUtc) }}</span>
          </b-col>
        </b-row>
      </div>

      <!-- Chat Input Area -->
      <div class="p-3 border-bottom" v-if="!isTicketClosed">
        <b-form-group label="Reply:" class="mb-2">
          <markdwn-editor
            ref="toastuiCommentEditor"
            :options="editorOptions"
            height="120px"
            initialEditType="wysiwyg"
            previewStyle="tab"
          ></markdwn-editor>
        </b-form-group>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <b-button
              v-if="!showAttachments"
              variant="outline-secondary"
              size="sm"
              @click="showAttachments = true"
            >
              <i class="fa fa-paperclip mr-1"></i> Attachments
            </b-button>
          </div>
          <b-button
            variant="primary"
            size="sm"
            :disabled="postingComment"
            @click="postComment"
          >
            <span v-if="postingComment">Sending...</span>
            <span v-else><i class="fa fa-paper-plane mr-1"></i> Send</span>
          </b-button>
        </div>
        <div v-if="showAttachments" class="mt-3">
          <UploadImages :max="5" @changed="handleImages" />
        </div>
      </div>
      <div v-else class="p-3 border-bottom">
        <b-alert show variant="warning" class="mb-0 d-flex justify-content-between align-items-center">
          <span>
            <i class="fa fa-lock mr-1"></i> This ticket is closed.
          </span>
          <b-button
            variant="warning"
            size="sm"
            @click="showReopenModal = true"
          >
            <i class="fa fa-refresh mr-1"></i> Reopen Ticket
          </b-button>
        </b-alert>
      </div>

      <!-- Reopen Modal -->
      <b-modal
        v-model="showReopenModal"
        title="Reopen Ticket"
        centered
        @ok="reopenTicket"
        :ok-disabled="!reopenReason.trim() || reopeningTicket"
        :ok-title="reopeningTicket ? 'Reopening...' : 'Reopen'"
        ok-variant="warning"
      >
        <b-form-group label="Why are you reopening this ticket?">
          <b-form-textarea
            v-model="reopenReason"
            placeholder="Enter reason for reopening..."
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </b-modal>

      <!-- Messages List -->
      <div class="chat-messages-container p-3" ref="messagesContainer">
        <div
          v-for="message in allMessages"
          :key="message.id"
          class="message-item mb-3"
        >
          <div class="d-flex align-items-start">
            <b-avatar
              size="32"
              :variant="message.isOriginal ? 'primary' : (message.userId === currentUserId ? 'info' : 'success')"
              :src="message.authorAvatar"
              class="mr-2"
            ></b-avatar>
            <div class="flex-grow-1">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="font-weight-bold small">
                  {{ message.authorName }}
                  <b-badge v-if="message.isOriginal" variant="primary" class="ml-1" style="font-size: 0.65rem;">Original</b-badge>
                </span>
                <small class="text-muted">{{ formatDate(message.createDateTimeUtc) }}</small>
              </div>
              <div class="message-content p-2 rounded ticket-message-content" :class="message.isOriginal ? 'bg-primary-light' : (message.userId !== currentUserId ? 'bg-light' : 'bg-primary-light')">
                <vue-markdown>{{ message.message }}</vue-markdown>
              </div>
              <div v-if="message.reactions && message.reactions.length" class="mt-1">
                <span
                  v-for="(reaction, idx) in message.reactions"
                  :key="idx"
                  class="badge badge-light border mr-1"
                  v-b-tooltip.hover
                  :title="reaction.byUser + ' reacted with :' + reaction.name + ':'"
                >
                  {{ getReactionEmoji(reaction.name) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import { Editor } from '@toast-ui/vue-editor';
import VueMarkdown from 'vue-markdown';
import UploadImages from 'vue-upload-drop-images';
import CustomEmojis from '@/helpers/custom-emojis.js';

var refOfHttp = null;
var refOfEditor = null;

var addImageBlobHook = function(blob, callback) {
  if (refOfEditor && refOfHttp) {
    var fileName = blob.name;
    var reader = new FileReader();
    reader.onload = function() {
      var formData = new FormData();
      formData.append('formFile', blob);
      formData.append('extension', fileName.split('.').pop());
      var headers = { 'Content-Type': 'multipart/form-data' };
      refOfHttp.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
        var oldContent = refOfEditor.invoke('getMarkdown');
        refOfEditor.invoke('setMarkdown', oldContent + '\r\n\r\n![' + fileName + '](' + resp.data.url + ')\r\n\r\n');
      });
    };
    reader.readAsDataURL(blob);
  }
  callback();
  return false;
};

export default {
  name: 'TicketPreviewSidebar',
  components: {
    'vue-markdown': VueMarkdown,
    'markdwn-editor': Editor,
    UploadImages
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    ticketId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      ticket: null,
      loading: false,
      commentMessage: '',
      postingComment: false,
      customEmojiList: CustomEmojis.list,
      reopenReason: '',
      reopeningTicket: false,
      showReopenModal: false,
      showAttachments: false,
      uploadedFiles: [],
      editorOptions: {
        hooks: {
          addImageBlobHook: addImageBlobHook
        },
        minHeight: '80px',
        language: 'en-US',
        useCommandShortcut: true,
        hideModeSwitch: true,
        toolbarItems: [
          ['bold', 'italic', 'strike'],
          ['ul', 'ol'],
          ['link', 'image'],
          ['code']
        ]
      }
    };
  },

  mounted: function() {
    var self = this;
    refOfHttp = this.$http;
    this.$nextTick(function() {
      if (self.$refs.toastuiCommentEditor) {
        refOfEditor = self.$refs.toastuiCommentEditor;
      }
    });
  },

  computed: {
    currentUserId() {
      return this.$store.getters['account/getUserData'].id;
    },
    isTicketClosed() {
      return this.ticket && (this.ticket.status === 'completed' || this.ticket.status === 'canceled');
    },
    allMessages: function() {
      var self = this;
      if (!self.ticket) return [];

      var messages = (self.ticket.ticketMessages || []).slice();

      // Add the original message as the last item (oldest)
      if (self.ticket.message) {
        messages.push({
          id: 'original-message',
          message: self.ticket.message,
          authorName: self.ticket.userFullName,
          authorEmail: self.ticket.userEmail,
          userId: self.ticket.userId,
          createDateTimeUtc: self.ticket.createDateTimeUtc,
          isOriginal: true,
          reactions: []
        });
      }

      return messages;
    }
  },

  watch: {
    ticketId: {
      handler: function(newVal) {
        if (newVal) {
          this.loadTicket(newVal);
        } else {
          this.ticket = null;
        }
      },
      immediate: true
    }
  },

  methods: {
    loadTicket(id) {
      this.loading = true;
      this.$http
        .get('/api/common/tickets/' + id)
        .then(resp => {
          this.ticket = resp.data;
        })
        .catch(err => {
          console.error('Failed to load ticket', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    postComment() {
      var self = this;
      if (!self.$refs.toastuiCommentEditor || !self.ticket) return;

      var message = self.$refs.toastuiCommentEditor.invoke('getMarkdown');
      if (!message || !message.trim()) {
        self.$swal('Error', 'Please enter a message', 'warning');
        return;
      }

      // Handle attachments
      if (self.uploadedFiles.length > 0) {
        message += '\n\n**Attachments:**';
        self.uploadedFiles.forEach(function(file) {
          message += '\n\n![' + file.name + '](' + file.url + ')';
        });
      }

      self.postingComment = true;
      var userData = self.$store.getters['account/getUserData'];
      var payload = {
        ticketId: self.ticket.id,
        message: message,
        authorEmail: userData.email,
        authorName: userData.fullName,
        userId: userData.id
      };

      self.$http
        .post('/api/common/ticketMessages', payload)
        .then(function(resp) {
          var newMessage = resp.data;
          if (!self.ticket.ticketMessages) {
            self.ticket.ticketMessages = [];
          }
          self.ticket.ticketMessages.unshift(newMessage);
          self.$refs.toastuiCommentEditor.invoke('setMarkdown', '');
          self.uploadedFiles = [];
          self.showAttachments = false;
        })
        .catch(function(err) {
          console.error('Failed to post comment', err);
          self.$swal('Error', 'Failed to send message', 'error');
        })
        .finally(function() {
          self.postingComment = false;
        });
    },

    handleImages: function(files) {
      var self = this;
      files.forEach(function(file) {
        if (!self.uploadedFiles.find(function(f) { return f.name === file.name; })) {
          var formData = new FormData();
          formData.append('formFile', file);
          var headers = { 'Content-Type': 'multipart/form-data' };
          self.$http.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
            self.uploadedFiles.push({
              name: file.name,
              size: file.size,
              url: resp.data.url
            });
          });
        }
      });
    },

    closeSidebar() {
      this.$emit('input', false);
    },

    onHidden() {
      this.$emit('input', false);
      this.$emit('closed');
      this.ticket = null;
      this.commentMessage = '';
      this.reopenReason = '';
      this.uploadedFiles = [];
      this.showAttachments = false;
    },

    reopenTicket(bvModalEvt) {
      var self = this;
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      if (!self.reopenReason.trim() || !self.ticket) return;

      self.reopeningTicket = true;
      var payload = {
        id: self.ticket.id,
        reason: self.reopenReason
      };

      self.$http
        .post('/api/common/tickets/reopen', payload)
        .then(function(resp) {
          self.ticket = resp.data;
          self.reopenReason = '';
          self.showReopenModal = false;
          self.$swal('Success', 'Ticket has been reopened', 'success');
        })
        .catch(function(err) {
          console.error('Failed to reopen ticket', err);
          self.$swal('Error', 'Failed to reopen ticket', 'error');
        })
        .finally(function() {
          self.reopeningTicket = false;
        });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      var date = dateStr;
      if (!dateStr.endsWith('Z')) {
        date = dateStr + 'Z';
      }
      return this.$moment(date).format('MMM Do YY, h:mm a');
    },

    getReactionEmoji(name) {
      var reactInfo = this.customEmojiList.find(function(a) {
        return a.aliases.includes(name);
      });
      if (reactInfo) {
        return reactInfo.data;
      }
      return name;
    },

    getPriorityLabel(priority) {
      var labels = {
        0: 'Not Set',
        1: 'Low',
        2: 'Medium',
        3: 'High',
        4: 'Urgent'
      };
      return labels[priority] || 'Not Set';
    },

    getPriorityVariant(priority) {
      var variants = {
        0: 'secondary',
        1: 'secondary',
        2: 'info',
        3: 'warning',
        4: 'danger'
      };
      return variants[priority] || 'secondary';
    },

    getStatusLabel(status, subStatus) {
      if (subStatus && subStatus !== '') {
        return subStatus;
      }
      var labels = {
        'triage': 'Triage',
        'backlog': 'Backlog',
        'unstarted': 'Open',
        'started': 'In Progress',
        'canceled': 'Cancelled',
        'completed': 'Completed'
      };
      return labels[status] || status;
    },

    getStatusVariant(status, subStatus) {
      if (subStatus && subStatus !== '') {
        return 'info';
      }
      var variants = {
        'triage': 'warning',
        'backlog': 'secondary',
        'unstarted': 'primary',
        'started': 'info',
        'canceled': 'danger',
        'completed': 'success'
      };
      return variants[status] || 'secondary';
    }
  }
};
</script>

<style scoped>
.chat-messages-container {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.message-content {
  word-wrap: break-word;
}

.bg-primary-light {
  background-color: #e3f2fd;
}

.message-item .vue-markdown p {
  margin-bottom: 0;
}
</style>
